
import { Component, Vue } from "vue-property-decorator";
import vSelectControl from "@/components/vSelect.vue";
import CouponsRepository from "@/utils/API/CouponsRepository";
import CancellationWorkflowRepository from "@/utils/API/CancellationWorkflowRepository";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import ListRequest from "@/models/ListRequest";
import { CouponType } from "@/models/Coupon/Coupon";
import {
  AddonOption,
  CancellationWorkflowConfigurationDto,
  CardConfiguration,
  createEmptyPackageCancellationWorkflowConfiguration,
  defaultAddonOptions,
  defaultGiftOptions,
  GiftOption,
  OfferOptionConfiguration,
  PackageConfiguration,
  resolveWorkflowConfiguration,
  serializeWorkflowConfigurations,
  SaveCancellationWorkflowConfigurationsDto,
} from "@/models/CancellationWorkflow";
import Package from "@/models/Subscriptions/SubscriptionPackages/Package";
import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";
import { SubscriptionRecurrenceType } from "@/models/Subscriptions/SubscriptionRecurrenceType";

const cancellationHeroImage = require("@/assets/img/admin-facing/chef.jpg");

type CouponOption = {
  code: string;
  description: string;
}

type PackageSelectOption = {
  id: number;
  name: string;
}

type SupportedAddonValue = AddOnType.AllergenAI | AddOnType.RecipeCost | AddOnType.SugarAlcohols | AddOnType.WellnessModule;

type GiftDurationDraft = {
  slotIndex: number;
  giftValue: string;
  amount: number | null;
  unit: "month" | "year" | "infinite";
}

type ParsedGiftDuration = {
  amount: number | null;
  unit: "month" | "year" | "infinite";
}

@Component({
  components: {
    vSelectControl,
  },
})
class CancellationWorkflow extends Vue {
  cancellationHeroImage = cancellationHeroImage;
  selectedPackageId: number | null = null;
  selectedCardId = "personalized-offer";
  selectedLighterOptionTab: "recommended" | "discount" = "recommended";
  activeGiftSlotIndex: number | null = null;
  activeGiftDurationDraft: GiftDurationDraft | null = null;
  hasUnsavedChanges = false;
  isLoadingSelectedPackage = false;
  lastSavedAt = "";
  latestPackageRequestId = 0;
  originalPackageConfigurations: PackageConfiguration[] = [];

  couponOptions: CouponOption[] = [
    { code: "SAVE15NEXT6", description: "" },
    { code: "SAVE40NEXT3", description: "" },
    { code: "SUPPORTMAKEGOOD", description: "" },
  ];

  addonOptions: AddonOption[] = defaultAddonOptions;

  giftOptions: GiftOption[] = defaultGiftOptions;

  packageConfigurations: PackageConfiguration[] = [];
  subscriptionPackages: Package[] = [];

  notifySuccess(message: string) {
    const snotify = (this as any).$snotify;
    if (snotify && snotify.success) {
      snotify.success(message);
    }
  }

  notifyError(message: string) {
    const snotify = (this as any).$snotify;
    if (snotify && snotify.error) {
      snotify.error(message);
    }
  }

  get notSavedYetLabel() {
    return "Not saved yet";
  }

  mounted() {
    this.loadCancellationCoupons();
    this.loadWorkflowData();
    this.lastSavedAt = this.notSavedYetLabel;
  }

  get packageSelectOptions(): PackageSelectOption[] {
    return this.subscriptionPackages
      .filter((pkg) => pkg.isActive)
      .sort((left, right) => (left.order || 0) - (right.order || 0))
      .map((pkg) => ({
      id: this.normalizePackageId(pkg.id) || 0,
      name: `#${this.normalizePackageId(pkg.id) || pkg.id} ${pkg.name}${this.getPackageCycleLabel(pkg.id)}`,
    }));
  }

  get selectedPackageSelectId() {
    if (this.selectedPackageId === null) {
      return null;
    }

    return this.subscriptionPackages.some(
      (pkg) => pkg.isActive && this.normalizePackageId(pkg.id) === this.selectedPackageId
    )
      ? this.selectedPackageId
      : null;
  }

  get couponSelectOptions() {
    return [
      { id: "", name: "No coupon" },
      ...this.couponOptions.map((coupon) => ({
        id: coupon.code,
        name: coupon.description ? `${coupon.code} - ${coupon.description}` : coupon.code,
      })),
    ];
  }

  get giftCountOptions() {
    return [
      { id: 0, name: "0 gifts" },
      { id: 1, name: "1 gift" },
      { id: 2, name: "2 gifts" },
      { id: 3, name: "3 gifts" },
    ];
  }

  get giftSelectOptions() {
    return this.giftOptions
      .filter((gift) => gift.value === "free-addon")
      .map((gift) => ({
      id: gift.value,
      name: gift.label,
    }));
  }

  get durationUnitOptions() {
    return [
      { id: "month", name: "Month" },
      { id: "year", name: "Year" },
      { id: "infinite", name: "Infinite" },
    ];
  }

  get retentionValidityUnitOptions() {
    return [
      { id: "month", name: "Month" },
      { id: "year", name: "Year" },
    ];
  }

  get selectedConfiguration() {
    return this.packageConfigurations.find((pkg) => pkg.id === this.selectedPackageId) || null;
  }

  get selectedCardConfiguration() {
    if (!this.selectedConfiguration) {
      return null;
    }

    return this.selectedConfiguration.cardConfigurations.find((card) => card.id === this.selectedCardId)
      || this.selectedConfiguration.cardConfigurations[0]
      || null;
  }

  get selectedSubscriptionPackage() {
    return this.subscriptionPackages.find((pkg) => this.normalizePackageId(pkg.id) === this.selectedPackageId) || null;
  }

  get selectedPackageDisplayName() {
    return this.selectedSubscriptionPackage?.name
      || "Cancellation Settings";
  }

  get isStarterPackageSelected() {
    return (this.selectedConfiguration?.packageName || this.selectedSubscriptionPackage?.name || "").toLowerCase().includes("starter");
  }

  get activeLighterOptionConfiguration() {
    if (!this.selectedCardConfiguration) {
      return null;
    }

    return this.selectedLighterOptionTab === "recommended"
      ? this.selectedCardConfiguration.recommendedOffer
      : this.selectedCardConfiguration.discountOffer;
  }

  get availableAddonOptions() {
    if (!this.selectedConfiguration) {
      return [];
    }

    return this.selectedConfiguration.availableAddons
      .map((addonValue) => this.addonOptions.find((addon) => addon.value === addonValue))
      .filter((addon): addon is AddonOption => Boolean(addon));
  }

  get activeAvailableAddonOptions() {
    if (this.activeLighterOptionConfiguration) {
      return this.activeLighterOptionConfiguration.availableAddons
        .map((addonValue) => this.addonOptions.find((addon) => addon.value === addonValue))
        .filter((addon): addon is AddonOption => Boolean(addon));
    }

    return this.availableAddonOptions;
  }

  get activeGiftSlotIndexes() {
    const targetGiftCount = Number(this.activeLighterOptionConfiguration?.giftCount || 0);

    return Array.from({ length: Number.isNaN(targetGiftCount) ? 0 : targetGiftCount }, (_, index) => index);
  }

  getCouponDescription(couponCode: string) {
    return this.couponOptions.find((coupon) => coupon.code === couponCode)?.description || "";
  }

  getSeededCouponDescription(couponCode: string) {
    switch (couponCode) {
    case "SAVE15NEXT6":
      return "15% off for the next 6 months";
    case "SAVE40NEXT3":
      return "40% off for the next 3 months";
    default:
      return "";
    }
  }

  loadCancellationCoupons() {
    CouponsRepository.fetchCoupons(
      new ListRequest({
        page: 1,
        pageSize: 250,
        filters: [{ name: "CouponType", value: String(CouponType.Cancellation) }],
        orders: [],
      })
    )
      .then((response) => {
        const loadedCoupons = (response.data.result || []).map((coupon: any): CouponOption => ({
          code: coupon.code,
          description: coupon.name || this.getSeededCouponDescription(coupon.code),
        }));

        if (loadedCoupons.length) {
          this.couponOptions = loadedCoupons;
        }
      })
      .catch(() => {
        // Keep seeded fallback options so the page remains usable if the coupon list call fails.
      });
  }

  loadWorkflowData() {
    SubscriptionRepository.displaySubscriptionPackages(true)
      .then((packagesResponse) => {
        const subscriptionPackages = ((packagesResponse.data && packagesResponse.data.result) || []) as Package[];
        this.subscriptionPackages = subscriptionPackages;
        this.originalPackageConfigurations = [];
        this.packageConfigurations = [];
        this.selectedPackageId = null;
        this.selectedCardId = "personalized-offer";

        const firstActivePackage = this.packageSelectOptions[0];
        if (firstActivePackage) {
          this.handlePackageSelect(firstActivePackage.id);
        }

      })
      .catch(() => {
        this.subscriptionPackages = [];
        this.originalPackageConfigurations = [];
        this.packageConfigurations = [];
        this.selectedPackageId = null;
        this.selectedCardId = "personalized-offer";
      });
  }

  getPackageCycleLabel(packageId: number) {
    const normalizedPackageId = this.normalizePackageId(packageId);
    const subscriptionPackage = this.subscriptionPackages.find(
      (pkg) => this.normalizePackageId(pkg.id) === normalizedPackageId
    );

    if (!subscriptionPackage) {
      return "";
    }

    return subscriptionPackage.cycle === SubscriptionRecurrenceType.Monthly
      ? " (monthly)"
      : " (yearly)";
  }

  syncConfigurationWithPackage(
    configuration: PackageConfiguration,
    subscriptionPackage: Package,
    overwriteAddonAvailability: boolean
  ) {
    const syncedConfiguration = JSON.parse(JSON.stringify(configuration)) as PackageConfiguration;
    const dbAddonCodes = this.getPackageAddonCodes(subscriptionPackage);
    const dbAddonCodeSet = new Set<number>(dbAddonCodes);

    syncedConfiguration.id = subscriptionPackage.id;
    syncedConfiguration.packageName = subscriptionPackage.name;
    syncedConfiguration.availableAddons = syncedConfiguration.availableAddons
      .map((addonValue) => this.normalizeAddonValue(addonValue))
      .filter((addonValue): addonValue is AddOnType => addonValue !== null);

    if (overwriteAddonAvailability) {
      syncedConfiguration.availableAddons = [...dbAddonCodes];
    } else if (dbAddonCodes.length) {
      syncedConfiguration.availableAddons = syncedConfiguration.availableAddons.filter((addonCode) => dbAddonCodeSet.has(addonCode as number));
    }

    syncedConfiguration.cardConfigurations = syncedConfiguration.cardConfigurations.map((cardConfiguration) => {
      const nextCardConfiguration = JSON.parse(JSON.stringify(cardConfiguration)) as CardConfiguration;
      nextCardConfiguration.recommendedOffer.availableAddons = nextCardConfiguration.recommendedOffer.availableAddons
        .map((addonValue) => this.normalizeAddonValue(addonValue))
        .filter((addonValue): addonValue is AddOnType => addonValue !== null);
      nextCardConfiguration.discountOffer.availableAddons = nextCardConfiguration.discountOffer.availableAddons
        .map((addonValue) => this.normalizeAddonValue(addonValue))
        .filter((addonValue): addonValue is AddOnType => addonValue !== null);

      if (dbAddonCodes.length || overwriteAddonAvailability) {
        nextCardConfiguration.recommendedOffer.availableAddons = overwriteAddonAvailability
          ? [...dbAddonCodes]
          : nextCardConfiguration.recommendedOffer.availableAddons.filter((addonCode) => dbAddonCodeSet.has(addonCode as number));

        nextCardConfiguration.discountOffer.availableAddons = overwriteAddonAvailability
          ? [...dbAddonCodes]
          : nextCardConfiguration.discountOffer.availableAddons.filter((addonCode) => dbAddonCodeSet.has(addonCode as number));
      }

      this.normalizeSingleGiftOptionSelection(nextCardConfiguration.recommendedOffer);
      this.normalizeSingleGiftOptionSelection(nextCardConfiguration.discountOffer);

      return nextCardConfiguration;
    });

    return syncedConfiguration;
  }

  getPackageAddonCodes(subscriptionPackage: Package): SupportedAddonValue[] {
    return (subscriptionPackage.addOns || [])
      .filter((addOn) => addOn && addOn.isActive !== false)
      .map((addOn) => {
        switch (addOn.addOn) {
        case AddOnType.AllergenAI:
          return AddOnType.AllergenAI;
        case AddOnType.RecipeCost:
          return AddOnType.RecipeCost;
        case AddOnType.SugarAlcohols:
          return AddOnType.SugarAlcohols;
        case AddOnType.WellnessModule:
          return AddOnType.WellnessModule;
        default:
          return null;
        }
      })
      .filter((addonCode): addonCode is SupportedAddonValue => addonCode !== null);
  }

  normalizeAddonValue(addonValue: AddOnType | string | null | undefined) {
    if (addonValue === null || addonValue === undefined) {
      return null;
    }

    if (typeof addonValue === "number") {
      return addonValue;
    }

    const normalizedAddonValue = addonValue.trim();

    if (normalizedAddonValue in AddOnType) {
      const enumValue = AddOnType[normalizedAddonValue as keyof typeof AddOnType];
      if (typeof enumValue === "number") {
        return enumValue;
      }
    }

    switch (normalizedAddonValue.toLowerCase()) {
    case "allergen-ai":
      return AddOnType.AllergenAI;
    case "recipe-cost":
      return AddOnType.RecipeCost;
    case "sugar-alcohols":
      return AddOnType.SugarAlcohols;
    case "wellness-analyzer":
      return AddOnType.WellnessModule;
    default:
      return null;
    }
  }

  getAddonDisplayName(addonValue: AddOnType | string) {
    const normalizedAddonValue = this.normalizeAddonValue(addonValue);

    if (normalizedAddonValue === null) {
      return String(addonValue);
    }

    return AddOnType[normalizedAddonValue] || String(addonValue);
  }

  fetchPackageConfiguration(packageId: number) {
    const subscriptionPackage = this.subscriptionPackages.find(
      (pkg) => this.normalizePackageId(pkg.id) === packageId
    );

    if (!subscriptionPackage) {
      return;
    }

    const requestId = ++this.latestPackageRequestId;
    this.isLoadingSelectedPackage = true;
    this.lastSavedAt = this.notSavedYetLabel;
    this.selectedCardId = "personalized-offer";
    this.activeGiftSlotIndex = null;
    this.activeGiftDurationDraft = null;

    CancellationWorkflowRepository.getConfiguration({ subscriptionPackageId: packageId })
      .then((response) => {
        const configurationDto = response.data.result as CancellationWorkflowConfigurationDto | null;
        let syncedConfiguration = this.syncConfigurationWithPackage(
          createEmptyPackageCancellationWorkflowConfiguration(subscriptionPackage.id, subscriptionPackage.name),
          subscriptionPackage,
          false
        );

        if (configurationDto?.configuration) {
          syncedConfiguration = this.syncConfigurationWithPackage(
            resolveWorkflowConfiguration(configurationDto.configuration, subscriptionPackage.id, subscriptionPackage.name),
            subscriptionPackage,
            false
          );
        }

        this.upsertPackageConfiguration(packageId, syncedConfiguration);

        this.selectedCardId = syncedConfiguration?.cardConfigurations[0]?.id || "personalized-offer";

        if (configurationDto?.updatedOn && this.selectedPackageId === packageId) {
          this.lastSavedAt = new Date(configurationDto.updatedOn).toLocaleString();
        } else if (this.selectedPackageId === packageId) {
          this.lastSavedAt = this.notSavedYetLabel;
        }
      })
      .catch(() => {
        const fallbackConfiguration = this.syncConfigurationWithPackage(
          createEmptyPackageCancellationWorkflowConfiguration(subscriptionPackage.id, subscriptionPackage.name),
          subscriptionPackage,
          false
        );

        this.upsertPackageConfiguration(packageId, fallbackConfiguration);
        this.selectedCardId = fallbackConfiguration.cardConfigurations[0]?.id || "personalized-offer";
        if (this.selectedPackageId === packageId) {
          this.lastSavedAt = this.notSavedYetLabel;
        }
      })
      .finally(() => {
        if (requestId === this.latestPackageRequestId && this.selectedPackageId === packageId) {
          this.isLoadingSelectedPackage = false;
        }
      });
  }

  ensurePackageConfiguration(packageId: number) {
    const existingConfiguration = this.packageConfigurations.find((pkg) => pkg.id === packageId);

    if (existingConfiguration) {
      this.selectedCardId = existingConfiguration.cardConfigurations[0]?.id || "personalized-offer";
      return existingConfiguration;
    }

    const subscriptionPackage = this.subscriptionPackages.find(
      (pkg) => this.normalizePackageId(pkg.id) === packageId
    );

    if (!subscriptionPackage) {
      return null;
    }

    const fallbackConfiguration = this.syncConfigurationWithPackage(
      createEmptyPackageCancellationWorkflowConfiguration(subscriptionPackage.id, subscriptionPackage.name),
      subscriptionPackage,
      false
    );

    this.packageConfigurations = [
      ...this.packageConfigurations.filter((pkg) => pkg.id !== packageId),
      fallbackConfiguration,
    ];

    this.selectedCardId = fallbackConfiguration.cardConfigurations[0]?.id || "personalized-offer";

    return fallbackConfiguration;
  }

  upsertPackageConfiguration(packageId: number, configuration: PackageConfiguration) {
    this.packageConfigurations = [
      ...this.packageConfigurations.filter((pkg) => pkg.id !== packageId),
      configuration,
    ];

    this.originalPackageConfigurations = [
      ...this.originalPackageConfigurations.filter((pkg) => pkg.id !== packageId),
      JSON.parse(JSON.stringify(configuration)) as PackageConfiguration,
    ];
  }

  getVisibleGifts(card: CardConfiguration) {
    return card.selectedGiftSlots
      .slice(0, card.giftCount)
      .map((giftValue) => this.giftOptions.find((gift) => gift.value === giftValue))
      .filter((gift): gift is GiftOption => Boolean(gift));
  }

  getGiftOptionByValue(giftValue: string | null) {
    if (!giftValue) {
      return null;
    }

    return this.giftOptions.find((gift) => gift.value === giftValue) || null;
  }

  getGiftSlotValues(card: CardConfiguration): Array<string | null> {
    return Array.from({ length: card.giftCount }, (_, index): string | null => card.selectedGiftSlots[index] || null);
  }

  getLighterOptionGiftSlotValues(option: OfferOptionConfiguration): Array<string | null> {
    return Array.from({ length: option.giftCount }, (_, index): string | null => option.selectedGiftSlots[index] || null);
  }

  getGiftSlotDuration(card: CardConfiguration, slotIndex: number) {
    return (card.selectedGiftDurations && card.selectedGiftDurations[slotIndex]) || "";
  }

  getLighterOptionGiftSlotDuration(option: OfferOptionConfiguration, slotIndex: number) {
    return (option.selectedGiftDurations && option.selectedGiftDurations[slotIndex]) || "";
  }

  parseGiftDuration(rawDuration: unknown): ParsedGiftDuration {
    const duration = typeof rawDuration === "string"
      ? rawDuration
      : typeof rawDuration === "number"
        ? String(rawDuration)
        : "";

    if (!duration || !duration.trim()) {
      return { amount: 1, unit: "month" as const };
    }

    if (duration.trim().toLowerCase() === "infinite") {
      return { amount: null, unit: "infinite" as const };
    }

    const normalizedDuration = duration.trim().replace(/\s+/g, " ");
    const matchedDuration = normalizedDuration.match(/^(\d+)\s+(month|months|year|years)$/i);

    if (matchedDuration) {
      return {
        amount: Number(matchedDuration[1]),
        unit: matchedDuration[2].toLowerCase().startsWith("year") ? "year" as const : "month" as const,
      };
    }

    const leadingNumberMatch = normalizedDuration.match(/^(\d+)/);
    if (leadingNumberMatch) {
      return {
        amount: Number(leadingNumberMatch[1]),
        unit: normalizedDuration.toLowerCase().includes("year") ? "year" as const : "month" as const,
      };
    }

    return { amount: 1, unit: "month" as const };
  }

  formatGiftDuration(amount: number | null, unit: "month" | "year" | "infinite") {
    if (unit === "infinite") {
      return "Infinite";
    }

    const safeAmount = amount && amount > 0 ? amount : 1;
    const label = unit === "year"
      ? (safeAmount === 1 ? "year" : "years")
      : (safeAmount === 1 ? "month" : "months");

    return `${safeAmount} ${label}`;
  }

  handlePackageSelect(packageId: number) {
    this.selectedPackageId = packageId;
    this.selectedLighterOptionTab = "recommended";
    this.activeGiftSlotIndex = null;
    this.activeGiftDurationDraft = null;
    this.ensurePackageConfiguration(packageId);
    this.fetchPackageConfiguration(packageId);
  }

  handlePackageSelectChange(event: Event) {
    const target = event.target as HTMLSelectElement | null;
    this.handlePackageSelectFromDropdown(target?.value ?? "");
  }

  onPackageFilterChange(value: unknown) {
    const normalizedValue = this.normalizeSelectValue(value);

    if (normalizedValue === "") {
      this.clearSelectedPackage();
      return;
    }

    this.handlePackageSelectFromDropdown(normalizedValue);
  }

  normalizePackageId(packageId: number | string | null | undefined) {
    if (packageId === null || packageId === undefined || packageId === "") {
      return null;
    }

    const normalizedPackageId = Number(packageId);
    return Number.isNaN(normalizedPackageId) ? null : normalizedPackageId;
  }

  normalizeSelectValue(value: unknown): string | number {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    if (typeof value === "string" || typeof value === "number") {
      return value;
    }

    if (typeof value === "object" && "id" in (value as Record<string, unknown>)) {
      const optionId = (value as { id?: string | number }).id;
      return optionId ?? "";
    }

    return "";
  }

  updateActiveCouponCode(couponCode: string | number | { id?: string | number } | null) {
    if (!this.activeLighterOptionConfiguration) {
      return;
    }

    const normalizedCouponCode = this.normalizeSelectValue(couponCode);
    this.activeLighterOptionConfiguration.couponCode = typeof normalizedCouponCode === "string"
      ? normalizedCouponCode
      : String(normalizedCouponCode || "");
    this.markDirty();
  }

  updateActiveGiftCount(giftCount: number | string | { id?: string | number } | null) {
    if (!this.activeLighterOptionConfiguration) {
      return;
    }

    const normalizedGiftCount = this.normalizeSelectValue(giftCount);
    this.activeLighterOptionConfiguration.giftCount = Number(normalizedGiftCount || 0);
    this.enforceActiveLighterOptionGiftCount();
    this.$forceUpdate();
  }

  updateValidityPeriodUnit(validityPeriodUnit: string | number | { id?: string | number } | null) {
    if (!this.activeLighterOptionConfiguration) {
      return;
    }

    const normalizedValue = this.normalizeSelectValue(validityPeriodUnit);
    this.activeLighterOptionConfiguration.validityPeriodUnit = normalizedValue === "year" ? "year" : "month";
    this.markDirty();
  }

  updateMinimumFinalPrice(rawValue: string) {
    if (!this.activeLighterOptionConfiguration) {
      return;
    }

    const parsedValue = Number(rawValue);
    this.activeLighterOptionConfiguration.minimumFinalPrice = Number.isFinite(parsedValue)
      ? parsedValue
      : null;
    this.markDirty();
  }

  clearSelectedPackage() {
    this.selectedPackageId = null;
    this.selectedCardId = "personalized-offer";
    this.selectedLighterOptionTab = "recommended";
    this.activeGiftSlotIndex = null;
    this.activeGiftDurationDraft = null;
    this.isLoadingSelectedPackage = false;
  }

  handlePackageSelectFromDropdown(packageId: number | string | null) {
    if (packageId === null || packageId === "") {
      this.clearSelectedPackage();
      return;
    }

    const parsedId = Number(packageId);

    if (Number.isNaN(parsedId)) {
      return;
    }

    this.handlePackageSelect(parsedId);
  }

  handleCouponChange(card: CardConfiguration) {
    this.markDirty();
  }

  normalizeSingleGiftOptionSelection(target: OfferOptionConfiguration) {
    this.ensureGiftDurationArray(target);

    for (let index = 0; index < 3; index += 1) {
      if (index < target.giftCount) {
        const nextGiftValue = target.selectedGiftSlots[index] || "free-addon";
        const nextDuration = target.selectedGiftDurations![index] || this.getDefaultGiftDuration(nextGiftValue);
        target.selectedGiftSlots.splice(index, 1, nextGiftValue);
        target.selectedGiftDurations!.splice(index, 1, nextDuration);
      } else {
        target.selectedGiftSlots.splice(index, 1, null);
        target.selectedGiftDurations!.splice(index, 1, "");
      }
    }
  }

  enforceGiftCount(card: CardConfiguration) {
    this.ensureGiftDurationArray(card);

    if (card.selectedGiftSlots.length < 3) {
      card.selectedGiftSlots = [
        ...card.selectedGiftSlots,
        ...Array.from({ length: 3 - card.selectedGiftSlots.length }, (): null => null),
      ];
    }

    card.selectedGiftSlots = card.selectedGiftSlots.slice(0, 3);
    card.selectedGiftDurations = card.selectedGiftDurations!.slice(0, 3);

    if (this.activeGiftSlotIndex !== null && this.activeGiftSlotIndex >= card.giftCount) {
      this.activeGiftSlotIndex = null;
      this.activeGiftDurationDraft = null;
    }
    this.markDirty();
  }

  enforceActiveLighterOptionGiftCount() {
    if (!this.activeLighterOptionConfiguration) {
      return;
    }

    this.ensureGiftDurationArray(this.activeLighterOptionConfiguration);

    if (this.activeLighterOptionConfiguration.selectedGiftSlots.length < 3) {
      this.activeLighterOptionConfiguration.selectedGiftSlots = [
        ...this.activeLighterOptionConfiguration.selectedGiftSlots,
        ...Array.from({ length: 3 - this.activeLighterOptionConfiguration.selectedGiftSlots.length }, (): null => null),
      ];
    }

    this.activeLighterOptionConfiguration.selectedGiftSlots = this.activeLighterOptionConfiguration.selectedGiftSlots.slice(0, 3);
    this.activeLighterOptionConfiguration.selectedGiftDurations = this.activeLighterOptionConfiguration.selectedGiftDurations!.slice(0, 3);

    for (let index = this.activeLighterOptionConfiguration.giftCount; index < 3; index += 1) {
      this.activeLighterOptionConfiguration.selectedGiftSlots.splice(index, 1, null);
      this.activeLighterOptionConfiguration.selectedGiftDurations!.splice(index, 1, "");
    }

    for (let index = 0; index < this.activeLighterOptionConfiguration.giftCount; index += 1) {
      const nextGiftValue = this.activeLighterOptionConfiguration.selectedGiftSlots[index] || "free-addon";
      const nextDuration = this.activeLighterOptionConfiguration.selectedGiftDurations![index] || this.getDefaultGiftDuration(nextGiftValue);
      this.activeLighterOptionConfiguration.selectedGiftSlots.splice(index, 1, nextGiftValue);
      this.activeLighterOptionConfiguration.selectedGiftDurations!.splice(index, 1, nextDuration);
    }

    if (this.activeGiftSlotIndex !== null && this.activeGiftSlotIndex >= this.activeLighterOptionConfiguration.giftCount) {
      this.activeGiftSlotIndex = null;
      this.activeGiftDurationDraft = null;
    }

    this.markDirty();
  }

  getGiftSelectionTarget() {
    return this.activeLighterOptionConfiguration || this.selectedCardConfiguration || null;
  }

  normalizeGiftSelections(target: CardConfiguration | OfferOptionConfiguration) {
    this.ensureGiftDurationArray(target);

    const selectedPairs = target.selectedGiftSlots
      .map((giftValue, index) => ({
        giftValue,
        duration: target.selectedGiftDurations ? target.selectedGiftDurations[index] || "" : "",
      }))
      .filter((item): item is { giftValue: string; duration: string } => Boolean(item.giftValue))
      .slice(0, 3);

    target.selectedGiftSlots = [
      ...selectedPairs.map((item) => item.giftValue),
      ...Array.from({ length: 3 - selectedPairs.length }, (): null => null),
    ];

    target.selectedGiftDurations = [
      ...selectedPairs.map((item) => item.duration || this.getDefaultGiftDuration(item.giftValue)),
      ...Array.from({ length: 3 - selectedPairs.length }, () => ""),
    ];
  }

  getSelectedGiftCount() {
    const target = this.getGiftSelectionTarget();

    if (!target) {
      return 0;
    }

    return target.selectedGiftSlots.filter((giftValue): giftValue is string => Boolean(giftValue)).length;
  }

  getSelectedGiftIndex(giftValue: string) {
    const target = this.getGiftSelectionTarget();

    if (!target) {
      return -1;
    }

    return target.selectedGiftSlots.findIndex((selectedGiftValue) => selectedGiftValue === giftValue);
  }

  isGiftSelected(giftValue: string) {
    return this.getSelectedGiftIndex(giftValue) >= 0;
  }

  isGiftSelectionDisabled(giftValue: string) {
    const target = this.getGiftSelectionTarget();

    if (!target) {
      return true;
    }

    if (this.isGiftSelected(giftValue)) {
      return false;
    }

    return this.getSelectedGiftCount() >= target.giftCount;
  }

  toggleGiftSelection(giftValue: string, isChecked: boolean) {
    const target = this.getGiftSelectionTarget();

    if (!target) {
      return;
    }

    this.ensureGiftDurationArray(target);
    this.normalizeGiftSelections(target);

    const existingIndex = target.selectedGiftSlots.findIndex((selectedGiftValue) => selectedGiftValue === giftValue);

    if (!isChecked) {
      if (existingIndex >= 0) {
        target.selectedGiftSlots.splice(existingIndex, 1, null);
        target.selectedGiftDurations!.splice(existingIndex, 1, "");
        this.normalizeGiftSelections(target);
        this.markDirty();
      }
      return;
    }

    if (existingIndex >= 0 || this.getSelectedGiftCount() >= target.giftCount) {
      return;
    }

    const nextOpenIndex = target.selectedGiftSlots.findIndex((selectedGiftValue) => !selectedGiftValue);

    if (nextOpenIndex === -1) {
      return;
    }

    target.selectedGiftSlots.splice(nextOpenIndex, 1, giftValue);
    target.selectedGiftDurations!.splice(nextOpenIndex, 1, this.getDefaultGiftDuration(giftValue));
    this.normalizeGiftSelections(target);
    this.markDirty();
  }

  getSelectedGiftDuration(giftValue: string) {
    const target = this.getGiftSelectionTarget();
    const selectedGiftIndex = this.getSelectedGiftIndex(giftValue);

    if (!target || selectedGiftIndex < 0) {
      return this.getDefaultGiftDuration(giftValue);
    }

    this.ensureGiftDurationArray(target);
    return target.selectedGiftDurations![selectedGiftIndex] || this.getDefaultGiftDuration(giftValue);
  }

  getSelectedGiftDurationAmount(giftValue: string) {
    const parsedDuration = this.parseGiftDuration(this.getSelectedGiftDuration(giftValue));

    return parsedDuration.unit === "infinite"
      ? 1
      : (parsedDuration.amount && parsedDuration.amount > 0 ? parsedDuration.amount : 1);
  }

  getSelectedGiftDurationUnit(giftValue: string) {
    return this.parseGiftDuration(this.getSelectedGiftDuration(giftValue)).unit;
  }

  setSelectedGiftDuration(giftValue: string, amount: number | null, unit: "month" | "year" | "infinite") {
    const target = this.getGiftSelectionTarget();
    const selectedGiftIndex = this.getSelectedGiftIndex(giftValue);

    if (!target || selectedGiftIndex < 0) {
      return;
    }

    this.ensureGiftDurationArray(target);
    target.selectedGiftDurations!.splice(
      selectedGiftIndex,
      1,
      unit === "infinite" ? "Infinite" : this.formatGiftDuration(amount, unit)
    );
    this.markDirty();
  }

  updateSelectedGiftDurationAmount(giftValue: string, rawAmount: string) {
    const numericAmount = Number(rawAmount);
    const nextAmount = Number.isFinite(numericAmount) && numericAmount > 0 ? numericAmount : 1;
    const currentUnit = this.getSelectedGiftDurationUnit(giftValue);

    this.setSelectedGiftDuration(giftValue, currentUnit === "infinite" ? null : nextAmount, currentUnit);
  }

  updateSelectedGiftDurationUnit(giftValue: string, rawUnit: string) {
    const normalizedUnit = rawUnit === "year" || rawUnit === "infinite" ? rawUnit : "month";
    const currentAmount = this.getSelectedGiftDurationAmount(giftValue);

    this.setSelectedGiftDuration(
      giftValue,
      normalizedUnit === "infinite" ? null : currentAmount,
      normalizedUnit
    );
  }

  toggleAvailableAddon(addonValue: AddOnType) {
    if (!this.activeLighterOptionConfiguration) {
      return;
    }
    const activeAddonIndex = this.activeLighterOptionConfiguration.availableAddons.indexOf(addonValue);

    if (activeAddonIndex >= 0) {
      this.activeLighterOptionConfiguration.availableAddons.splice(activeAddonIndex, 1);
      this.markDirty();
      return;
    }

    if (this.activeLighterOptionConfiguration.availableAddons.length >= this.addonOptions.length) {
      return;
    }

    this.activeLighterOptionConfiguration.availableAddons.push(addonValue);
    this.markDirty();
  }

  updateGiftSlotSelection(slotIndex: number, giftValue: string | number | { id?: string | number } | null) {
    if (!this.activeLighterOptionConfiguration) {
      return;
    }

    this.ensureGiftDurationArray(this.activeLighterOptionConfiguration);
    const normalizedGiftValue = this.normalizeSelectValue(giftValue);
    const nextGiftValue = typeof normalizedGiftValue === "string"
      ? normalizedGiftValue
      : String(normalizedGiftValue || "");
    const allowsDuplicateGiftSelection = nextGiftValue === "free-addon";
    const existingGiftIndex = giftValue && !allowsDuplicateGiftSelection
      ? this.activeLighterOptionConfiguration.selectedGiftSlots.findIndex(
        (selectedGiftValue, index) => selectedGiftValue === nextGiftValue && index !== slotIndex
      )
      : -1;

    let nextDuration = nextGiftValue
      ? this.getDefaultGiftDuration(nextGiftValue)
      : "";

    if (existingGiftIndex >= 0) {
      nextDuration = this.activeLighterOptionConfiguration.selectedGiftDurations![existingGiftIndex] || nextDuration;
      this.activeLighterOptionConfiguration.selectedGiftSlots.splice(existingGiftIndex, 1, null);
      this.activeLighterOptionConfiguration.selectedGiftDurations!.splice(existingGiftIndex, 1, "");
    } else if (nextGiftValue === this.activeLighterOptionConfiguration.selectedGiftSlots[slotIndex]) {
      nextDuration = this.activeLighterOptionConfiguration.selectedGiftDurations![slotIndex] || nextDuration;
    }

    this.activeLighterOptionConfiguration.selectedGiftSlots.splice(slotIndex, 1, nextGiftValue || null);
    this.activeLighterOptionConfiguration.selectedGiftDurations!.splice(slotIndex, 1, nextGiftValue ? nextDuration : "");
    this.markDirty();
  }

  getGiftSlotDurationAmount(slotIndex: number) {
    if (!this.activeLighterOptionConfiguration) {
      return 1;
    }

    const parsedDuration = this.parseGiftDuration(
      this.getLighterOptionGiftSlotDuration(this.activeLighterOptionConfiguration, slotIndex)
    );

    return parsedDuration.unit === "infinite"
      ? 1
      : (parsedDuration.amount && parsedDuration.amount > 0 ? parsedDuration.amount : 1);
  }

  getGiftSlotDurationUnitValue(slotIndex: number) {
    if (!this.activeLighterOptionConfiguration) {
      return "month";
    }

    return this.parseGiftDuration(
      this.getLighterOptionGiftSlotDuration(this.activeLighterOptionConfiguration, slotIndex)
    ).unit;
  }

  updateGiftSlotDurationAmount(slotIndex: number, rawAmount: string) {
    if (!this.activeLighterOptionConfiguration || !this.activeLighterOptionConfiguration.selectedGiftSlots[slotIndex]) {
      return;
    }

    const parsedAmount = Number(rawAmount);
    const normalizedAmount = Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : 1;
    this.ensureGiftDurationArray(this.activeLighterOptionConfiguration);
    this.activeLighterOptionConfiguration.selectedGiftDurations!.splice(
      slotIndex,
      1,
      this.formatGiftDuration(normalizedAmount, "month")
    );
    this.markDirty();
  }

  updateGiftSlotDurationUnit(slotIndex: number, rawUnit: string | number | { id?: string | number } | null) {
    if (!this.activeLighterOptionConfiguration || !this.activeLighterOptionConfiguration.selectedGiftSlots[slotIndex]) {
      return;
    }

    const normalizedUnitValue = this.normalizeSelectValue(rawUnit);
    const unit = normalizedUnitValue === "year" || normalizedUnitValue === "infinite" ? normalizedUnitValue : "month";
    const amount = this.getGiftSlotDurationAmount(slotIndex);
    this.ensureGiftDurationArray(this.activeLighterOptionConfiguration);
    this.activeLighterOptionConfiguration.selectedGiftDurations!.splice(
      slotIndex,
      1,
      unit === "infinite" ? "Infinite" : this.formatGiftDuration(amount, unit)
    );
    this.markDirty();
  }

  openGiftSlot(slotIndex: number) {
    if (this.activeGiftSlotIndex === slotIndex && !this.activeGiftDurationDraft) {
      this.activeGiftSlotIndex = null;
      return;
    }

    this.activeGiftSlotIndex = this.activeGiftSlotIndex === slotIndex ? null : slotIndex;
    this.activeGiftDurationDraft = null;
  }

  isGiftSlotOpen(slotIndex: number) {
    return this.activeGiftSlotIndex === slotIndex && !this.activeGiftDurationDraft;
  }

  isGiftDurationOpen(slotIndex: number) {
    return this.activeGiftSlotIndex === slotIndex && this.activeGiftDurationDraft?.slotIndex === slotIndex;
  }

  startGiftDurationSelection(slotIndex: number, giftValue: string) {
    const currentDuration = this.activeLighterOptionConfiguration
      ? this.getLighterOptionGiftSlotDuration(this.activeLighterOptionConfiguration, slotIndex)
      : this.getGiftSlotDuration(this.selectedCardConfiguration, slotIndex);
    const parsedDuration = this.parseGiftDuration(currentDuration || this.getDefaultGiftDuration(giftValue));

    this.activeGiftSlotIndex = slotIndex;
    this.activeGiftDurationDraft = {
      slotIndex,
      giftValue,
      amount: parsedDuration.amount,
      unit: parsedDuration.unit,
    };
  }

  getDefaultGiftDuration(giftValue: string) {
    if (giftValue === "free-month-extension" || giftValue === "free-addon") {
      return "1 month";
    }

    return "6 months";
  }

  saveGiftWithDuration() {
    if (!this.selectedCardConfiguration || !this.activeGiftDurationDraft) {
      return;
    }

    const { slotIndex, giftValue } = this.activeGiftDurationDraft;
    const duration = this.activeGiftDurationDraft.unit === "infinite"
      ? "Infinite"
      : this.formatGiftDuration(this.activeGiftDurationDraft.amount, this.activeGiftDurationDraft.unit);

    if (this.activeLighterOptionConfiguration) {
      this.ensureGiftDurationArray(this.activeLighterOptionConfiguration);
      this.activeLighterOptionConfiguration.selectedGiftSlots.splice(slotIndex, 1, giftValue);
      this.activeLighterOptionConfiguration.selectedGiftDurations!.splice(slotIndex, 1, duration);
    } else {
      this.ensureGiftDurationArray(this.selectedCardConfiguration);
      this.selectedCardConfiguration.selectedGiftSlots.splice(slotIndex, 1, giftValue);
      this.selectedCardConfiguration.selectedGiftDurations!.splice(slotIndex, 1, duration);
    }

    this.activeGiftSlotIndex = null;
    this.activeGiftDurationDraft = null;
    this.markDirty();
  }

  backToGiftSelection() {
    this.activeGiftDurationDraft = null;
  }

  clearGiftSlot(slotIndex: number) {
    if (!this.selectedCardConfiguration) {
      return;
    }

    if (this.activeLighterOptionConfiguration) {
      this.ensureGiftDurationArray(this.activeLighterOptionConfiguration);
      this.activeLighterOptionConfiguration.selectedGiftSlots.splice(slotIndex, 1, null);
      this.activeLighterOptionConfiguration.selectedGiftDurations!.splice(slotIndex, 1, "");
      this.activeGiftSlotIndex = null;
      this.activeGiftDurationDraft = null;
      this.markDirty();
      return;
    }

    this.ensureGiftDurationArray(this.selectedCardConfiguration);
    this.selectedCardConfiguration.selectedGiftSlots.splice(slotIndex, 1, null);
    this.selectedCardConfiguration.selectedGiftDurations!.splice(slotIndex, 1, "");
    this.activeGiftSlotIndex = null;
    this.activeGiftDurationDraft = null;
    this.markDirty();
  }

  ensureGiftDurationArray(target: CardConfiguration | OfferOptionConfiguration) {
    if (!target.selectedGiftDurations) {
      this.$set(target, "selectedGiftDurations", ["", "", ""]);
      return;
    }

    if (target.selectedGiftDurations.length < 3) {
      target.selectedGiftDurations = [
        ...target.selectedGiftDurations,
        ...Array.from({ length: 3 - target.selectedGiftDurations.length }, () => ""),
      ];
    }
  }

  markDirty() {
    this.hasUnsavedChanges = true;
  }

  openRetentionReport() {
    this.$router.push({ name: "retention-report" });
  }

  saveConfiguration() {
    const payload: SaveCancellationWorkflowConfigurationsDto = {
      configurations: serializeWorkflowConfigurations(this.packageConfigurations),
    };

    CancellationWorkflowRepository.saveConfigurations(payload)
      .then(() => {
        this.hasUnsavedChanges = false;
        this.lastSavedAt = new Date().toLocaleString();
        this.originalPackageConfigurations = JSON.parse(JSON.stringify(this.packageConfigurations));
        this.notifySuccess("Cancellation workflow saved.");
      })
      .catch(() => {
        this.notifyError("Unable to save cancellation workflow settings.");
      });
  }

  resetDraft() {
    this.packageConfigurations = JSON.parse(JSON.stringify(this.originalPackageConfigurations));
    this.selectedCardId = this.selectedConfiguration?.cardConfigurations[0]?.id || "personalized-offer";
    this.selectedLighterOptionTab = "recommended";
    this.activeGiftSlotIndex = null;
    this.activeGiftDurationDraft = null;
    this.hasUnsavedChanges = false;
  }
}

export default CancellationWorkflow;
